import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import BusinessCenterIcon from "@material-ui/icons/BusinessCenter";

import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import crmService from "../../services/crm";
import { useModuleAccess } from "../../context/ModuleAccess/ModuleAccessContext";

const EMPTY_FORM = {
  title: "",
  pipelineId: "",
  stageId: "",
  amount: "",
  expectedCloseAt: "",
  description: "",
};

const useStyles = makeStyles((theme) => ({
  crmActionWrapper: {
    display: "inline-flex",
  },
  crmAction: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: "transparent",
    color: theme.mode === "light" ? theme.palette.primary.main : "#FFF",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor:
        theme.mode === "light"
          ? "rgba(8, 114, 185, 0.06)"
          : "rgba(255, 255, 255, 0.08)",
      transform: "translateY(-1px)",
    },
  },
}));

const getActiveStages = (pipeline) =>
  (pipeline?.stages || [])
    .filter((stage) => stage.isActive !== false)
    .sort(
      (left, right) =>
        Number(left.order || 0) - Number(right.order || 0) ||
        Number(left.id) - Number(right.id),
    );

const TicketCrmAction = ({ ticket, contact }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [links, setLinks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pipelines, setPipelines] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const moduleAccess = useModuleAccess();
  const canAccessCrm = !moduleAccess.loading && moduleAccess.features.crm?.allowed === true;

  const selectedPipeline = useMemo(
    () =>
      pipelines.find(
        (pipeline) => Number(pipeline.id) === Number(form.pipelineId),
      ),
    [form.pipelineId, pipelines],
  );
  const stages = useMemo(
    () => getActiveStages(selectedPipeline),
    [selectedPipeline],
  );
  const primaryDeal = links[0]?.deal;
  const actionLabel = primaryDeal?.id ? "Abrir negócio" : "Criar negócio";

  const loadLinks = useCallback(async () => {
    if (
      !ticket?.id ||
      !["admin", "user"].includes(user?.profile) ||
      !canAccessCrm
    ) {
      setVisible(false);
      return;
    }

    setLoading(true);
    try {
      const result = await crmService.listTicketDeals(ticket.id);
      setLinks(result?.links || []);
      setVisible(true);
    } catch (error) {
      const code = error?.response?.data?.error;
      if (
        code === "ERR_CRM_DISABLED_FOR_COMPANY" ||
        code === "ERR_CRM_PERMISSION_DENIED" ||
        code === "ERR_CRM_TICKET_PERMISSION_DENIED" ||
        code === "ERR_NO_CONTACT_PERMISSION"
      ) {
        setVisible(false);
      } else {
        toastError(error);
      }
    } finally {
      setLoading(false);
    }
  }, [canAccessCrm, ticket?.id, user?.profile]);

  useEffect(() => {
    setLinks([]);
    setDialogOpen(false);
    loadLinks();
  }, [loadLinks]);

  const openCreateDialog = async () => {
    setLoading(true);
    try {
      const result = await crmService.listPipelines();
      const activePipelines = (Array.isArray(result) ? result : []).filter(
        (pipeline) => pipeline.isActive !== false,
      );
      const defaultPipeline =
        activePipelines.find((pipeline) => pipeline.isDefault) ||
        activePipelines[0];
      const defaultStage = getActiveStages(defaultPipeline)[0];

      if (!defaultPipeline || !defaultStage) {
        toast.warn(
          "Cadastre um funil com uma etapa ativa antes de criar o negocio.",
        );
        return;
      }

      setPipelines(activePipelines);
      setForm({
        ...EMPTY_FORM,
        title: contact?.name ? `Negocio - ${contact.name}` : "Novo negocio",
        pipelineId: defaultPipeline.id,
        stageId: defaultStage.id,
      });
      setDialogOpen(true);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = () => {
    if (primaryDeal?.id) {
      history.push(`/crm?dealId=${primaryDeal.id}`);
      return;
    }

    openCreateDialog();
  };

  const handlePipelineChange = (event) => {
    const pipelineId = event.target.value;
    const pipeline = pipelines.find(
      (item) => Number(item.id) === Number(pipelineId),
    );
    setForm((current) => ({
      ...current,
      pipelineId,
      stageId: getActiveStages(pipeline)[0]?.id || "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.pipelineId || !form.stageId) return;

    setSaving(true);
    try {
      const deal = await crmService.createDealFromTicket(ticket.id, {
        title: form.title.trim(),
        pipelineId: form.pipelineId,
        stageId: form.stageId,
        amount: form.amount === "" ? 0 : Number(form.amount),
        currency: "BRL",
        expectedCloseAt: form.expectedCloseAt || null,
        description: form.description.trim() || null,
      });
      setLinks([{ id: `created-${deal.id}`, deal }]);
      setDialogOpen(false);
      toast.success("Negocio criado e vinculado a esta conversa.");
    } catch (error) {
      const code = error?.response?.data?.error;
      if (code === "ERR_CRM_TICKET_ALREADY_LINKED") {
        toast.info("Esta conversa ja possui um negocio vinculado.");
        setDialogOpen(false);
        await loadLinks();
      } else {
        toastError(error);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!visible || !canAccessCrm) return null;

  return (
    <>
      <Tooltip title={actionLabel}>
        <span className={classes.crmActionWrapper}>
          <IconButton
            aria-label={actionLabel}
            className={classes.crmAction}
            size="small"
            onClick={handleAction}
            disabled={loading || !ticket?.id}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <BusinessCenterIcon />
            )}
          </IconButton>
        </span>
      </Tooltip>

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle>Criar negocio pela conversa</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Contato: {contact?.name || "-"}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  variant="outlined"
                  label="Titulo"
                  value={form.title}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      title: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={7}>
                <TextField
                  select
                  fullWidth
                  required
                  size="small"
                  variant="outlined"
                  label="Funil"
                  value={form.pipelineId}
                  onChange={handlePipelineChange}
                >
                  {pipelines.map((pipeline) => (
                    <MenuItem key={pipeline.id} value={pipeline.id}>
                      {pipeline.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  select
                  fullWidth
                  required
                  size="small"
                  variant="outlined"
                  label="Etapa"
                  value={form.stageId}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      stageId: event.target.value,
                    }))
                  }
                >
                  {stages.map((stage) => (
                    <MenuItem key={stage.id} value={stage.id}>
                      {stage.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  type="number"
                  label="Valor"
                  inputProps={{ min: 0, step: "0.01" }}
                  value={form.amount}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      amount: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  type="date"
                  label="Previsao de fechamento"
                  InputLabelProps={{ shrink: true }}
                  value={form.expectedCloseAt}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      expectedCloseAt: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  variant="outlined"
                  label="Descricao"
                  value={form.description}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={
                saving ||
                !form.title.trim() ||
                !form.pipelineId ||
                !form.stageId
              }
            >
              {saving ? "Criando..." : "Criar negocio"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default TicketCrmAction;
